/**
 * Native Bridge for iOS Kiosk App
 * Handles communication with the native iOS wrapper
 *
 * The iOS app injects window.WrestlingIQKiosk which provides:
 * - addListener(type, callback) - register for events
 * - removeListener(type) - unregister events
 * - handleMessage(type, data) - called by native to dispatch to listeners
 */

export function useNativeBridge(store, apiService, notificationManager, onWrestlerFound) {
  let isSetup = false;

  function isNativeApp() {
    return typeof window !== 'undefined' && window.WrestlingIQKiosk;
  }

  function handleQRCodeScanned(scannedValue) {
    // Same logic as barcode scanner - QR codes use same athlete identifier format
    if (store.state.debug) {
      console.log('QR code scanned:', scannedValue);
    }

    if (store.state.lookingUpId) {
      notificationManager.emit('show-toast', 'Please wait, still looking up previously scanned id', false);
      return;
    }

    store.state.lookingUpId = true;

    const url = `${apiService.athleteIdsUrl()}?scanned=${encodeURIComponent(scannedValue)}`;

    axios.get(url)
      .then((response) => {
        if (response.data.athlete_identifiers.length > 0) {
          const athleteId = response.data.athlete_identifiers[0];
          if (onWrestlerFound) {
            onWrestlerFound(athleteId.profile);
          }
        } else {
          notificationManager.emit('show-toast', 'Unable to find QR code - please try again', false);
        }
        store.state.lookingUpId = false;
      })
      .catch((error) => {
        store.state.lookingUpId = false;
        notificationManager.emit('show-toast', error.toString(), false);
      });
  }

  function setupBridge() {
    if (!isNativeApp() || isSetup) {
      if (store.state.debug) {
        console.log('Native bridge setup skipped - not in native app or already setup');
      }
      return;
    }

    window.WrestlingIQKiosk.addListener('qrCodeScanned', handleQRCodeScanned);
    isSetup = true;

    if (store.state.debug) {
      console.log('Native bridge setup complete');
    }
  }

  function teardownBridge() {
    if (!isNativeApp() || !isSetup) {
      return;
    }

    window.WrestlingIQKiosk.removeListener('qrCodeScanned');
    isSetup = false;

    if (store.state.debug) {
      console.log('Native bridge teardown complete');
    }
  }

  /**
   * Notify native app when the camera tile should be hidden.
   *
   * Named setModalOpen for historical reasons — it was originally wired up to
   * just modal state, but callers now also flip it true when the user is
   * actively typing in the wrestler search (they've committed to name-search,
   * so the camera preview is just obstructing content).
   *
   * If the native contract ever gains a dedicated setCameraTileHidden, swap
   * this call for it and rename.
   */
  function setModalOpen(isOpen) {
    if (!isNativeApp()) {
      return;
    }

    window.WrestlingIQKiosk.setModalOpen(isOpen);

    if (store.state.debug) {
      console.log('Native bridge setModalOpen:', isOpen);
    }
  }

  /**
   * Request native app to show the keyboard
   * Used when programmatic focus() can't trigger keyboard (e.g., after timer-based auto-return)
   */
  function requestShowKeyboard() {
    if (!isNativeApp()) {
      return;
    }

    if (typeof window.WrestlingIQKiosk?.showKeyboard === 'function') {
      window.WrestlingIQKiosk.showKeyboard();

      if (store.state.debug) {
        console.log('Native bridge requestShowKeyboard called');
      }
    }
  }

  return {
    isNativeApp,
    setupBridge,
    teardownBridge,
    setModalOpen,
    requestShowKeyboard,
  };
}
